import ReactApexChart from "react-apexcharts";
import { Container, useMediaQuery, Box, Switch, Typography, Tooltip } from "@mui/material";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import ApexCharts from "apexcharts";
import FacebookCircularProgress from "../../shared/shared/Loading";
import { getUrl } from "src/Utils/Common";
import useRefreshData from "src/Utils/CustomHooks/useRefreshData";
import DateRangePicker from "./DatePicker";
import { DropDownDashboard } from "../../../Pages/Dashboard/DropDownDashboard";
import CustomSelectBox from "src/Components/shared/shared/CustomSelectBox";
import { theme } from "../../../Theme";

function BrandSales({
  selectedHierarchy: propSelectedHierarchy,
  setSelectedHierarchy: propSetSelectedHierarchy,
  timePeriod: propTimePeriod,
  setTimePeriod: propSetTimePeriod,
  selectedTab: propSelectedTab,
  setSelectedTab: propSetSelectedTab,
  dropDownValue: propDropDownValue,
  setDropDownValue: propSetDropDownValue,
  menuConfig: propMenuConfig,
  title,
  onDataLoaded,
  ...props
}) {
  const [localTimePeriod, setLocalTimePeriod] = useState(
    propTimePeriod !== undefined ? propTimePeriod : "P12M"
  );
  const timePeriod =
    propSetTimePeriod !== undefined ? propTimePeriod : localTimePeriod;
  const setTimePeriod =
    propSetTimePeriod !== undefined ? propSetTimePeriod : setLocalTimePeriod;

  const [localSelectedTab, setLocalSelectedTab] = useState(
    propSelectedTab !== undefined ? propSelectedTab : 0
  );
  const selectedTab =
    propSetSelectedTab !== undefined ? propSelectedTab : localSelectedTab;
  const setSelectedTab =
    propSetSelectedTab !== undefined ? propSetSelectedTab : setLocalSelectedTab;

  const [localSelectedHierarchy, setLocalSelectedHierarchy] = useState(
    propSelectedHierarchy !== undefined ? propSelectedHierarchy : "class"
  );
  const selectedHierarchy =
    propSetSelectedHierarchy !== undefined ? propSelectedHierarchy : localSelectedHierarchy;
  const setSelectedHierarchy =
    propSetSelectedHierarchy !== undefined ? propSetSelectedHierarchy : setLocalSelectedHierarchy;

  const [localDropDownValue, setLocalDropDownValue] = useState(
    propDropDownValue !== undefined ? propDropDownValue : "All Market"
  );
  const dropDownValue =
    propSetDropDownValue !== undefined ? propDropDownValue : localDropDownValue;
  const setDropDownValue =
    propSetDropDownValue !== undefined ? propSetDropDownValue : setLocalDropDownValue;

  const [hierarchyOptions, setHierarchyOptions] = useState([]);
  const [localMenuConfig, setLocalMenuConfig] = useState({});
  const menuConfig =
    propMenuConfig && Object.keys(propMenuConfig).length > 0
      ? propMenuConfig
      : localMenuConfig;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);

  const [paddedMax, setPaddedMax] = useState(0);
  const [chartTooltipEnabled, setChartTooltipEnabled] = useState(true);
  const currentChartType = useSelector((state) => state.chart.currentChartType);
  const selectedFilter = useSelector((state) => state.filter.selectedFilter);
  const currentChartTypePerformance = useSelector(
    (state) => state.brandPerformanceChart.currentChartTypePerformance
  );
  useEffect(() => {
    console.log(`Current chart type in Dashboard: ${currentChartType}`);
  }, [currentChartType, selectedHierarchy]);

  const currentDateComparison = useSelector(
    (state) => state.dateCompare.currentDateComparison
  );

  // Get projectId from Redux store
  const projectId = useSelector((state) => state.userProject.projectId);

  const chartContainerRef = useRef(null);
  const latestRequestIdRef = useRef(0);

  const [data, setData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [hiddenSeries, setHiddenSeries] = useState(() => new Set());
  const chartId = "market-trends-brand-sales";

  const getApiFilters = () => {
    if (!selectedFilter || selectedFilter.length === 0) return {};

    return selectedFilter.reduce((acc, item) => {
      const { category, name } = item;

      if (!acc[category]) {
        acc[category] = []; // Initialize category array if not exists
      }

      acc[category].push(name); // Push the name value
      return acc;
    }, {});
  };
  

  async function fetchData() {
      const requestId = ++latestRequestIdRef.current;
      if (!projectId) {
        console.warn("Project ID is not set in Redux state.");
        if (latestRequestIdRef.current === requestId) setLoading(false);
        return; // Exit if projectId is not available
      }

      if (!selectedHierarchy) {
        if (latestRequestIdRef.current === requestId) {
          setError("Hierarchy not selected");
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      setError(null);

      // ✅ First, check currentChartType and assign hierarchy values
      let [hierarchy, hierarchy_value] = currentChartType?.split(",") || [
        "ALL Market",
        "All Market",
      ];

      // ✅ Then, check currentChartTypePerformance and override if it exists
      if (currentChartTypePerformance) {
        [, hierarchy_value] = currentChartTypePerformance.split(",");
      }
      if (!hierarchy || !hierarchy_value) {
        console.warn(
          "Invalid chart type format. Expected 'hierarchy,hierarchy_value'."
        );
        if (latestRequestIdRef.current === requestId) {
          setError("Invalid chart type format");
          setLoading(false);
        }
        return; // Exit if hierarchy or hierarchy_value is not available
      }
      // Override hierarchy with selectedHierarchy if provided

        // body: JSON.stringify({
        //   project_id: projectId,
        //   hierarchy,
        //   hierarchy_value,
        //   start_date: startDate.format("YYYY-MM-DD"), // Send selected start date
        //   end_date: endDate.format("YYYY-MM-DD"), // Send selected end date
        //   filters: getApiFilters(),
        // }),
        if (dropDownValue && dropDownValue !== "All Market") {
          hierarchy_value = dropDownValue;
        } else if (hierarchy_value === "All Market") {
          hierarchy_value = null;
        }
        hierarchy = selectedHierarchy;

        fetch(`${getUrl()}/market_overview/brand_performance`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-access-token": localStorage.getItem("userId") || "",
              "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
            },
            body: JSON.stringify({
              project_id: projectId,
              hierarchy,
              hierarchy_value,
              time_period: timePeriod,
              target: null,
              area: null,
              is_manufacturer: false,
              filters: getApiFilters(),
            }),
          }
        )
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.json();
        })
        .then((result) => {
          if (latestRequestIdRef.current !== requestId) return;
          if (
            result &&
            typeof result === "object" &&
            typeof result.debug === "string" &&
            result.debug.toLowerCase().includes("sql returned 0 rows")
          ) {
            setData([]);
            setCategories([]);
            setHasInitiallyLoaded(true);
            if (onDataLoaded) onDataLoaded([]);
            return;
          }

          const insights = result.insights;
          delete result.insights;
          // Extract date categories separately
          const xAxisCategories = result?.date || [];
          console.log(result.date);
          const brandKeys = Object.keys(result).filter((k) => k !== "date");

          const allValues = brandKeys.flatMap((key) => result[key]);

          if (allValues.length === 0) {
            setData([]);
            setCategories([]);
            setLoading(false);
            return;
          }

          const maxValue = Math.max(...allValues);
          setPaddedMax(Math.ceil(maxValue * 1.1)); // +10% buffer
          const transformedData = Object.entries(result)
            .filter(([key]) => key !== "date" && key !== "index")
            .map(([key, values], index) => ({
              name: key,
              type:
                hierarchy_value === null
                  ? key === "Total Market"
                    ? "line"
                    : "bar"
                  : key === hierarchy_value
                  ? "line"
                  : "bar",
              data: values,
            }));

          // Ensure at least one line exists when "All Market" is selected
          if (
            hierarchy_value === null &&
            transformedData.length > 0 &&
            !transformedData.some((d) => d.type === "line")
          ) {
            transformedData[0].type = "line"; // Set first dataset as line if no "Total Market"
          }
          // Update state
          console.log(transformedData);
          setData(transformedData);
          setCategories(xAxisCategories);
          setHasInitiallyLoaded(true);

          if (onDataLoaded) {
            const formatted = xAxisCategories.map((date, i) => {
              const obj = { date };
              brandKeys.forEach(key => {
                obj[key] = result[key][i];
              });
              return obj;
            });
            onDataLoaded(formatted);
          }
      }) 
      .catch ((error) => {
        if (latestRequestIdRef.current !== requestId) return;
        console.error("Error fetching data:", error);
        setError(error.message);
      })
      .finally(() => {
        if (latestRequestIdRef.current !== requestId) return;
        setLoading(false); 
      });
  }

  useEffect(() => {
    fetchData();
  }, [
    currentChartType,
    currentDateComparison,
    projectId,
    selectedFilter,
    currentChartTypePerformance,
    timePeriod,
    selectedHierarchy,
    dropDownValue,
  ]);

  // Reset functions for the chart
  const resetChartData = () => {
    setData([]);
    setCategories([]);
    setPaddedMax(0);
  };

  const resetChartFilters = () => {
    setLocalTimePeriod("P12M");
    setLocalSelectedTab(0);
  };

  const resetChartError = () => {
    setError(null);
  };

  const resetChartLoading = () => {
    setLoading(false);
  };

  const { handleRefresh, isRefreshing } = useRefreshData(
    fetchData,
    [resetChartData, resetChartFilters, resetChartError, resetChartLoading]
  );

  useEffect(() => {
    if (!projectId) return;

    fetch(`${getUrl()}/market_overview/hierarchy`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-access-token": localStorage.getItem("userId") || "",
        "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
      },
      body: JSON.stringify({ project_id: projectId }),
    })
      .then((response) => {
        if (!response.ok)
          throw new Error(`HTTP error! Status: ${response.status}`);
        return response.json();
      })
      .then((result) => {
        const hierarchyData =
          result?.["0"] ||
          result?.Product ||
          result?.product ||
          {};

        const hierarchyLevels = Object.keys(hierarchyData);
        const filteredHierarchyLevels = hierarchyLevels.filter(
          (level) =>
            !["size_bracket", "size", "price_bracket", "sku_db", "sku_id"].includes(level)
        );
        setHierarchyOptions([...filteredHierarchyLevels, "channel", "retailer"]);

        if (!propMenuConfig || Object.keys(propMenuConfig).length === 0) {
          const dynamicMenuConfig = { "All Market": ["All Market"] };
          Object.entries(hierarchyData).forEach(([key, values]) => {
            if (
              !["size_bracket", "size", "price_bracket", "sku_db", "sku_id"].includes(key)
            ) {
              dynamicMenuConfig[key] = values;
            }
          });
          dynamicMenuConfig["channel"] = [];
          dynamicMenuConfig["retailer"] = [];
          setLocalMenuConfig(dynamicMenuConfig);
        }

        setSelectedHierarchy((prev) => {
          if (
            !prev ||
            !filteredHierarchyLevels.includes(prev)
          ) {
            const savedValue = propSelectedHierarchy;
            return savedValue && filteredHierarchyLevels.includes(savedValue)
              ? savedValue
              : filteredHierarchyLevels.includes("brand")
              ? "brand"
              : filteredHierarchyLevels[0] || "";
          }
          return prev;
        });
      })
      .catch((error) =>
        console.error("Error fetching hierarchy levels:", error)
      );
  }, [projectId]);

  useEffect(() => {
    const rootEl = chartContainerRef.current;
    if (!rootEl) return;

    let isDownloadMenuOpen = false;

    const handleRootClick = (event) => {
      const menuIcon = rootEl.querySelector(".apexcharts-menu-icon");
      if (!menuIcon) return;

      if (menuIcon === event.target || menuIcon.contains(event.target)) {
        isDownloadMenuOpen = !isDownloadMenuOpen;
      }
    };

    const handleDocumentMouseDown = (event) => {
      const menuIcon = rootEl.querySelector(".apexcharts-menu-icon");

      if (
        menuIcon &&
        (menuIcon === event.target || menuIcon.contains(event.target))
      ) {
        return;
      }

      if (!isDownloadMenuOpen) {
        return;
      }

      const menuEl = rootEl.querySelector(".apexcharts-menu");

      if (menuEl && menuEl.contains(event.target)) {
        return;
      }

      if (menuIcon) {
        menuIcon.click();
        isDownloadMenuOpen = false;
      }
    };

    rootEl.addEventListener("click", handleRootClick);
    document.addEventListener("mousedown", handleDocumentMouseDown);

    return () => {
      rootEl.removeEventListener("click", handleRootClick);
      document.removeEventListener("mousedown", handleDocumentMouseDown);
    };
  }, []);

  const getYAxisFormatter = (val) => {
    if (val >= 1_000_000) {
      return (val / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
    } else if (val >= 1_000) {
      return (val / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
    } else {
      const formattedAmount = new Intl.NumberFormat("en-US", {
        maximumFractionDigits: 0,
      }).format(val);
      return formattedAmount;
    }
    // return val;
  };

  const [options] = useState({
    chart: {
      id: chartId,
      stacked: true,
      toolbar: {
        show: true,
        tools: {
          zoom: true,             
          zoomin: true,           
          zoomout: true,          
          pan: true,              
          reset: true,            
        },
        offsetX: 0,
        offsetY: 0,
        export: {
          csv: {
            filename: "brand-sales-data",
            columnDelimiter: ',',
            headerCategory: 'Date',
            headerValue: 'Value Sales (SR)',
          },
          svg: {
            filename: "brand-sales-chart",
          },
          png: {
            filename: "brand-sales-chart",
          },
        },
        autoSelected: 'pan',      
      },
      animations: {
        enabled: false,
        animateGradually: {
          enabled: false,
        },
        dynamicAnimation: {
          enabled: false,
        },
      },
      brush: {
        enabled: false,             
      },
      redrawOnParentResize: true,   
      redrawOnWindowResize: true,  
      zoom: {
        enabled: true,
        type: 'x',               
        autoScaleYaxis: true,     
        zoomedArea: {
          fill: {
            color: '#90CAF9',
            opacity: 0.4
          },
          stroke: {
            color: '#0D47A1',
            opacity: 0.4,
            width: 1
          }
        }
      },
      pan: {
        enabled: true,
        type: 'x',                  
        modifierKey: null,          
        threshold: 10,              
        range: {
          min: undefined,           
          max: undefined            
        }
      },
    },
    dataLabels: {
      enabled: false,
    },
    markers: {
      size: 4,
    },
    colors: ["#255e91", "#53b5ff", "#8acaec", "#B9DFF4", "#DCEFF9", "#de7c7e"],
    xaxis: {
      categories: categories,
      tooltip: {
        enabled: true,
      },
      axisBorder: {
        show: false,
      },
      range: undefined,              
      min: undefined,                
      max: undefined,                
      tickAmount: undefined,         
      floating: false,               
      labels: {
        rotate: -45,                
        rotateAlways: false,         
        hideOverlappingLabels: true, 
        showDuplicates: false,      
      },
    },
    grid: {
      show: true,
      borderColor: "#DCDCDC",
      position: "back",
    },
    yaxis: {
      seriesName: "bar",
      min: 0,
      max: paddedMax,
      title: {
        text: "Value Sales (SR)",
      },
      labels: {
        formatter: getYAxisFormatter,
      },
    },
    plotOptions: {
      bar: {
        columnWidth: "25%",
        borderRadius: 0,
        dataLabels: {
          position: "top",
        },
      },
    },
    stroke: {
        width: 6, 
        curve: "smooth",
      },
    fill: {
      opacity: [1, 1, 1, 1, 1], // Line (first series): no fill
    },
    legend: {
      show: false,
      position: "top",
      fontSize: "12px",
      fontWeight: 500,
      markers: {
        width: 12,
        height: 12,
        radius: 12,
      },
      itemMargin: {
        horizontal: 5,
        vertical: 5,
      },
    },
    responsive: [
      {
        breakpoint: 1920,
        options: {
          chart: { width: "900px" },
          legend: {
            position: "top",
            fontSize: "12px",
            markers: {
              width: 12,
              height: 12,
              radius: 12,
            },
            itemMargin: {
              horizontal: 5,
              vertical: 5,
            },
          },
        },
      },
      {
        breakpoint: 1350,
        options: {
          chart: { width: "100%" },
          legend: {
            position: "bottom",
            fontSize: "12px",
            markers: {
              width: 10,
              height: 10,
              radius: 10,
            },
            itemMargin: {
              horizontal: 8,
              vertical: 4,
            },
            offsetY: -10,
          },
          toolbar: {
            offsetY: 10,
          },
        },
      },
      {
        breakpoint: 1200,
        options: {
          chart: { width: "100%" },
          legend: {
            position: "bottom",
            fontSize: "11px",
            markers: {
              width: 9,
              height: 9,
              radius: 9,
            },
            itemMargin: {
              horizontal: 7,
              vertical: 3,
            },
            offsetY: -10,
          },
          toolbar: {
            offsetY: 10,
          },
        },
      },
      {
        breakpoint: 900,
        options: {
          chart: { width: "100%" },
          legend: {
            position: "bottom",
            fontSize: "11px",
            markers: {
              width: 8,
              height: 8,
              radius: 8,
            },
            itemMargin: {
              horizontal: 6,
              vertical: 3,
            },
            offsetY: -10,
          },
          toolbar: {
            offsetY: 10,
          },
        },
      },
      {
        breakpoint: 768,
        options: {
          chart: { width: "100%" },
          legend: { 
            position: "bottom",
            fontSize: "10px",
            markers: {
              width: 7,
              height: 7,
              radius: 7,
            },
            itemMargin: {
              horizontal: 5,
              vertical: 3,
            },
          },
          xaxis: {
            labels: { 
              rotate: -30,
              style: {
                fontSize: "10px",
              },
            },
          },
        },
      },
      {
        breakpoint: 600,
        options: {
          chart: { width: "100%" },
          legend: { 
            position: "bottom",
            fontSize: "10px",
            markers: {
              width: 6,
              height: 6,
              radius: 6,
            },
            itemMargin: {
              horizontal: 4,
              vertical: 2,
            },
          },
          xaxis: {
            labels: { 
              rotate: -45,
              style: {
                fontSize: "10px",
              },
            },
          },
        },
      },
      {
        breakpoint: 500,
        options: {
          chart: { width: "95%" },
          legend: { 
            position: "bottom",
            fontSize: "9px",
            markers: {
              width: 5,
              height: 5,
              radius: 1,
            },
            itemMargin: {
              horizontal: 3,
              vertical: 2,
            },
          },
          xaxis: {
            labels: { 
              rotate: -45,
              style: {
                fontSize: "9px",
              },
            },
          },
        },
      },
      {
        breakpoint: 480,
        options: {
          chart: { width: "90%" },
          legend: { 
            position: "bottom",
            fontSize: "9px",
            markers: {
              width: 5,
              height: 5,
              radius: 1,
            },
            itemMargin: {
              horizontal: 3,
              vertical: 2,
            },
          },
          xaxis: {
            labels: { 
              rotate: -45,
              style: {
                fontSize: "9px",
              },
            },
          },
        },
      },
      {
        breakpoint: 400,
        options: {
          chart: { width: "85%" },
          legend: { 
            position: "bottom",
            fontSize: "8px",
            markers: {
              width: 4,
              height: 4,
              radius: 1,
            },
            itemMargin: {
              horizontal: 2,
              vertical: 1,
            },
          },
          xaxis: {
            labels: { 
              rotate: -45,
              style: {
                fontSize: "8px",
              },
            },
          },
        },
      },
    ],
  });

  const isMobile = useMediaQuery("(max-width: 600px)");
  const isSmallScreen = useMediaQuery("(max-width: 1250px)");

  const legendTooltipSlotProps = {
    popper: {
      sx: {
        "& .MuiTooltip-tooltip": {
          bgcolor: "rgba(32, 38, 48, 0.97)",
          color: "#f0f4f8",
          fontSize: "0.8125rem",
          fontWeight: 500,
          lineHeight: 1.45,
          px: 1.75,
          py: 1.125,
          borderRadius: "10px",
          boxShadow: "0 10px 32px rgba(0, 0, 0, 0.24)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          maxWidth: 288,
        },
        "& .MuiTooltip-arrow": {
          color: "rgba(32, 38, 48, 0.97)",
        },
      },
    },
  };

  const seriesColors = options?.colors || [];
  const seriesNames = useMemo(() => data.map((s) => s.name), [data]);
  const allSeriesHidden =
    seriesNames.length > 0 && seriesNames.every((name) => hiddenSeries.has(name));

  const syncHiddenSeries = (updater) => {
    setHiddenSeries((prev) => {
      const next = new Set(prev);
      updater(next);
      return next;
    });
  };

  const toggleOneSeries = (name) => {
    if (!name) return;
    ApexCharts.exec(chartId, "toggleSeries", name);
    syncHiddenSeries((set) => {
      if (set.has(name)) set.delete(name);
      else set.add(name);
    });
  };

  const toggleAllSeries = () => {
    if (seriesNames.length === 0) return;

    if (allSeriesHidden) {
      seriesNames.forEach((name) => ApexCharts.exec(chartId, "showSeries", name));
      setHiddenSeries(new Set());
      return;
    }

    seriesNames.forEach((name) => ApexCharts.exec(chartId, "hideSeries", name));
    setHiddenSeries(new Set(seriesNames));
  };

  useEffect(() => {
    if (!seriesNames.length) {
      setHiddenSeries(new Set());
      return;
    }
    seriesNames.forEach((name) => ApexCharts.exec(chartId, "showSeries", name));
    setHiddenSeries(new Set());
  }, [chartId, seriesNames]);

  return (
    <>
      {/* Title + Filters row */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          mb: 2,
        }}
      >
        <Typography
          sx={{
            fontWeight: "bold",
            fontSize: { xs: "0.9rem", sm: "1rem", md: "1.2rem" },
            px: { xs: 0.5, sm: 0.75, md: 1 },
            color: theme.palette.primary.main,
            textAlign: "left",
            m: 0,
            lineHeight: 1.5,
            display: "flex",
            alignItems: "center",
            height: 48,
          }}
        >
          {title || "Market Trends"}
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: { xs: "6px", sm: "8px", md: "12px" },
            alignItems: "center",
            flexWrap: "wrap",
            justifyContent: { xs: "center", sm: "flex-start" },
          }}
        >
          <Box
            sx={{
              height: { xs: 32, sm: 36, md: 40 },
              display: "flex",
              alignItems: "center",
            }}
          >
            <DropDownDashboard
              value={dropDownValue}
              menuConfig={menuConfig}
              local={true}
              variant="local"
              defaultLabel="All Market"
              onChange={(newVal) => setDropDownValue(newVal)}
              triggerSx={{
                fontWeight: 500,
                fontSize: { xs: "14px", sm: "16px", md: "18px" },
                height: { xs: 32, sm: 36, md: 40 },
                minHeight: { xs: 32, sm: 36, md: 40 },
                maxHeight: { xs: 32, sm: 36, md: 40 },
                pb: { xs: 1, sm: 1.5, md: 2 },
                display: "flex",
                alignItems: "center",
              }}
            />
          </Box>

          <Typography
            sx={{
              color: "#0a00c2",
              fontSize: { xs: "16px", sm: "18px", md: "20px" },
              fontWeight: 500,
              mr: { xs: 1.5, sm: 2, md: 3 },
            }}
          >
            |
          </Typography>

          <CustomSelectBox
            value={hierarchyOptions.includes(selectedHierarchy) ? selectedHierarchy : ""}
            onChange={(event) => setSelectedHierarchy(event.target.value)}
            options={hierarchyOptions}
            sx={{
              fontWeight: 500,
              fontSize: { xs: "14px", sm: "16px", md: "18px" },
              height: { xs: 32, sm: 36, md: 40 },
              minHeight: { xs: 32, sm: 36, md: 40 },
              maxHeight: { xs: 32, sm: 36, md: 40 },
              p: 0,
              display: "flex",
              alignItems: "center",
            }}
          />
        </Box>
      </Box>

      <Box display="flex" flexDirection="column" gap={2} px={1} mb={2}>
        {/* <Typography fontWeight={theme.typography.fontWeightBold}>
          Brand Performance
        </Typography> */}

        <Container sx={{ flex: 1, minWidth: 0 }}>
          <DateRangePicker
            setTimePeriod={setTimePeriod}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            variant="yearly"
          />
        </Container>
      </Box>

      {!loading && !isRefreshing && data.length > 0 && (
        <Box
          sx={{
            display: "grid",
            alignItems: "center",
            gap: 1,
            mb: 1,
            px: 1,
            gridTemplateColumns: {
              xs: "1fr",
              md: "1fr auto 1fr",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              alignItems: "center",
              justifyContent: "center",
              minWidth: 0,
              gridColumn: { xs: "1", md: "2" },
            }}
          >
            <Tooltip
              title={
                <Box component="span" sx={{ display: "block", textAlign: "left" }}>
                  <Typography
                    component="span"
                    sx={{
                      display: "block",
                      fontWeight: 600,
                      fontSize: "0.8125rem",
                      color: "inherit",
                      mb: 0.35,
                    }}
                  >
                    {allSeriesHidden ? "Show all series" : "Hide all series"}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      display: "block",
                      fontSize: "0.75rem",
                      fontWeight: 400,
                      color: "rgba(255,255,255,0.82)",
                      lineHeight: 1.4,
                    }}
                  >
                    {allSeriesHidden
                      ? "Bring every brand back onto the chart."
                      : "Remove every brand from the chart at once."}
                  </Typography>
                </Box>
              }
              arrow
              placement="top"
              enterDelay={200}
              leaveDelay={0}
              slotProps={legendTooltipSlotProps}
            >
              <Box
                component="span"
                role="button"
                tabIndex={0}
                onClick={toggleAllSeries}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") toggleAllSeries();
                }}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  cursor: "pointer",
                  userSelect: "none",
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: allSeriesHidden
                      ? "rgba(0,0,0,0.35)"
                      : "rgba(0,0,0,0.65)",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 500,
                    opacity: allSeriesHidden ? 0.65 : 1,
                  }}
                >
                  All
                </Typography>
              </Box>
            </Tooltip>

            {data.map((s, idx) => {
              const isHidden = hiddenSeries.has(s.name);
              const color = seriesColors[idx % seriesColors.length] || "#999";
              return (
                <Tooltip
                  key={s.name}
                  title={
                    <Box component="span" sx={{ display: "block", textAlign: "left" }}>
                      <Typography
                        component="span"
                        sx={{
                          display: "block",
                          fontWeight: 600,
                          fontSize: "0.8125rem",
                          color: "inherit",
                          mb: 0.35,
                        }}
                      >
                        {isHidden ? `Show “${s.name}”` : `Hide “${s.name}”`}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          display: "block",
                          fontSize: "0.75rem",
                          fontWeight: 400,
                          color: "rgba(255,255,255,0.82)",
                          lineHeight: 1.4,
                        }}
                      >
                        {isHidden
                          ? "Add this series back to the chart."
                          : "Remove this series from the chart."}
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="top"
                  enterDelay={200}
                  leaveDelay={0}
                  slotProps={legendTooltipSlotProps}
                >
                  <Box
                    component="span"
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleOneSeries(s.name)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") toggleOneSeries(s.name);
                    }}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.75,
                      cursor: "pointer",
                      userSelect: "none",
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                    }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: color,
                        opacity: isHidden ? 0.25 : 1,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 500,
                        opacity: isHidden ? 0.5 : 1,
                        textDecoration: isHidden ? "line-through" : "none",
                      }}
                    >
                      {s.name}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>

          <Tooltip
            title={
              isMobile
                ? "Shows or hides the values popup when you hover over the chart"
                : "Shows or hides the popup that displays each bar's values when you hover over the chart"
            }
            arrow
            placement={isSmallScreen ? "bottom" : "left"}
            slotProps={{
              popper: {
                sx: {
                  "& .MuiTooltip-tooltip": {
                    maxWidth: isMobile ? 220 : 320,
                    fontSize: isMobile ? "0.75rem" : "0.9rem",
                  },
                },
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                justifyContent: { xs: "center", md: "flex-end" },
                gridColumn: { xs: "1", md: "3" },
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.7rem",
                  color: chartTooltipEnabled ? "primary.main" : "text.secondary",
                  fontWeight: 500,
                  minWidth: 28,
                  "@media (max-width: 900px)": {
                    fontSize: "0.65rem",
                    minWidth: 24,
                  },
                  "@media (max-width: 600px)": {
                    fontSize: "0.6rem",
                    minWidth: 22,
                  },
                }}
              >
                Show bar details
              </Typography>
              <Switch
                size="small"
                checked={chartTooltipEnabled}
                onChange={(e) => setChartTooltipEnabled(e.target.checked)}
                color="primary"
                sx={{
                  "@media (max-width: 900px)": {
                    transform: "scale(0.85)",
                    "& .MuiSwitch-thumb": { width: 14, height: 14 },
                    "& .MuiSwitch-switchBase": { padding: "4px" },
                  },
                  "@media (max-width: 600px)": {
                    transform: "scale(0.75)",
                    "& .MuiSwitch-thumb": { width: 12, height: 12 },
                    "& .MuiSwitch-switchBase": { padding: "3px" },
                  },
                }}
              />
            </Box>
          </Tooltip>
        </Box>
      )}

      <Box
        ref={chartContainerRef}
        sx={(theme) => ({
          width: "100%",
          minHeight: isMobile ? 250 : 455,
          position: "relative",
          "& .apexcharts-toolbar": {
            transform: "scale(1)",
            transformOrigin: "top right",
          },
          "& .apexcharts-toolbar > div:nth-child(3)": {
            transform: "scale(1.1)",
            transformOrigin: "center center",
          },
          [theme.breakpoints.down("md")]: {
            "& .apexcharts-toolbar": {
              transform: "scale(0.85)",
              transformOrigin: "top right",
            },
            "& .apexcharts-toolbar > div:nth-child(3)": {
              transform: "scale(1.1)",
              transformOrigin: "center center",
            },
          },
          [theme.breakpoints.down("sm")]: {
            "& .apexcharts-toolbar": {
              transform: "scale(0.72)",
              transformOrigin: "top right",
            },
            "& .apexcharts-toolbar > div:nth-child(3)": {
              transform: "scale(1.1)",
              transformOrigin: "center center",
            },
          },
        })}
      >
        {/* tooltip toggle moved into header row above */}
        {loading || isRefreshing ? (
          <Box
            sx={{
              width: isMobile ? "100%" : "100%",
              height: isMobile ? 250 : 450,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              paddingTop: isMobile ? "30px" : "50px",
              margin: "0 auto",
            }}
          >
            <FacebookCircularProgress />
          </Box>
        ) : error ? (
          // Show error message with refresh button
          <Box
            sx={{
              width: isMobile ? "100%" : "100%",
              height: isMobile ? 250 : 450,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto",
            }}
          >
            <FacebookCircularProgress
              loading={false}
              hasData={false}
              onRefresh={handleRefresh}
              message={`${error} - Click to reset chart`}
            />
          </Box>
        ) : data.length > 0 ? (
          // Show chart when we have data
          <ReactApexChart
            options={{
              ...options,
              tooltip: { enabled: chartTooltipEnabled },
              yaxis: {
                ...options.yaxis,
                max: paddedMax,
              },
              xaxis: {
                ...options.xaxis,
                categories,
                tooltip: { enabled: chartTooltipEnabled },
              },
              stroke: {
                ...options.stroke,
                width: data.map((series) => (series.type === "line" ? 2 : 0)),
              },
            }}
            series={data}
            type="line"
            height={isMobile ? 250 : 450}
            width={isMobile ? "100%" : "100%"}
          />
        ) : !hasInitiallyLoaded ? (
          <Box
            sx={{
              width: isMobile ? "100%" : "100%",
              height: isMobile ? 250 : 450,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              paddingTop: isMobile ? "30px" : "50px",
              margin: "0 auto",
            }}
          >
            <FacebookCircularProgress />
          </Box>
        ) : (
          // Show refresh button when no data
          <Box
            sx={{
              width: isMobile ? "100%" : "100%",
              height: isMobile ? 250 : 450,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto",
            }}
          >
            <FacebookCircularProgress
              loading={false}
              hasData={false}
              onRefresh={handleRefresh}
              message="No data available - Click to reset chart"
            />
          </Box>
        )}
      </Box>
    </>
  );
}

export default BrandSales;
